import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export const GalleryLightbox = ({ images, index, onClose, onNavigate, resolveImg }) => {
  const hasImages = Array.isArray(images) && images.length > 0;
  const isOpen = index !== null && index !== undefined && hasImages;

  const goPrev = useCallback(() => {
    onNavigate((index - 1 + images.length) % images.length);
  }, [index, images, onNavigate]);

  const goNext = useCallback(() => {
    onNavigate((index + 1) % images.length);
  }, [index, images, onNavigate]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, goPrev, goNext, onClose]);

  if (!isOpen) return null;

  const current = images[index];

  return (
    <div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in-up"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close-btn"
        aria-label="Close"
        onClick={onClose}
        className="absolute top-5 right-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-all duration-300 hover:bg-[#D4AF37] hover:scale-110"
      >
        <X className="h-6 w-6" />
      </button>

      {images.length > 1 && (
        <button
          data-testid="lightbox-prev-btn"
          aria-label="Previous image"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-3 md:left-8 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-all duration-300 hover:bg-[#D4AF37] hover:scale-110"
        >
          <ChevronLeft className="h-7 w-7" />
        </button>
      )}

      <figure
        className="flex max-h-[90vh] max-w-[92vw] flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          data-testid="lightbox-image"
          src={resolveImg(current.image)}
          alt={current.title}
          className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
        />
        <figcaption className="mt-4 text-center text-base font-medium text-white">
          {current.title}
          <span className="ml-3 text-sm text-[#D4AF37]">{index + 1} / {images.length}</span>
        </figcaption>
      </figure>

      {images.length > 1 && (
        <button
          data-testid="lightbox-next-btn"
          aria-label="Next image"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-3 md:right-8 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-all duration-300 hover:bg-[#D4AF37] hover:scale-110"
        >
          <ChevronRight className="h-7 w-7" />
        </button>
      )}
    </div>
  );
};

export default GalleryLightbox;
